<template>
  <div class="flex flex-wrap gap-x-3 gap-y-6 pt-5 pb-4 xl:hidden">
    <button class="my-auto cursor-pointer" 
            aria-label="Open menu" 
            aria-controls="mobile-menu"
            :aria-expanded="isVisible"
            @click="toggleMenu"
    >
      <Icon class="text-2xl" :icon="isVisible ? 'times' : 'bars'"/>
    </button>

    <NavLogo />
    <NavCartButton />

    <div class="flex justify-center items-center gap-x-2 basis-full">
      <NavSearchBar :show-taia-tooltip="!isVisible" 
                    :placeholder="isVisible ? 'Search' : 'Search products, articles, and more'"
      />
    </div>

    <Transition enter-active-class="transition-transform duration-400 ease-[ease] -z-50"
                leave-active-class="transition-transform duration-400 ease-[ease] -z-50"
                enter-from-class="transform -translate-y-full"
                leave-to-class="transform -translate-y-full"
    >
      <div v-show="isVisible" 
           id="mobile-menu"
           class="absolute left-0 top-[99%] w-full bg-white overflow-scroll md:max-h-fit"
           ref="menuRef"
           :style="menuStyling"
           @focusout="menuLoseFocus"
      >
        <div class="breakpoint-driven-widths py-4">
          <div class="relative">
            <div class="w-fit">
              <ul class="[&>li]:not-last:mb-6 [&_.link]:text-3xl">
                <template v-for="(navLink, index) in navLinkList.filter(link => link.label != null && link.url != null)">
                  <MobileNavMegaMenuButton v-if="navLink.megaMenuOptions?.enabled" 
                                          :id="`mobile-nav-link-${index}`"
                                          ref="megaMenuButtons"
                                          v-bind="navLink" 
                  />
                  <NavMenuLink v-else 
                              :id="`mobile-nav-link-${index}`"
                              @click="onLinkClicked" 
                              v-bind="navLink" 
                  />
                </template>
              </ul>

              <hr class="my-6 text-gray-300">
            </div>

            <div class="text-xl">
              <div class="flex flex-col gap-y-6" v-if="isLoggedIn">
                <span>{{ userName }}</span>
                <NuxtLink to="/account/settings/personal" external>
                  Account Settings
                </NuxtLink>
                <NuxtLink to="/logout" class="flex gap-x-1 items-center whitespace-nowrap" external>
                  Sign Out
                </NuxtLink>
              </div>
              <NuxtLink
                :to="{ path: '/login', query: { returnTo: route.fullPath } }"
                class="flex gap-x-1 items-center whitespace-nowrap"
                external
                v-else
              >
                Sign In
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">

import NavMenuLink from "../atom/navMenuLink.vue";
import NavCartButton from '../atom/navCartButton.vue';
import { type BuilderNavLink } from '../organism/navBar.vue';
import Icon from '../atom/icon.vue';
import NavLogo from '../atom/navLogo.vue';
import NavSearchBar from '../atom/navSearchBar.vue';
import MobileNavMegaMenuButton from '../atom/mobileNavMegaMenuButton.vue';
import { useIframeStore } from '~/stores/chatAIStore';


interface MobileNavMenuProps {
  navLinkList?: BuilderNavLink[]
  isLoggedIn?: boolean
  userName?: string
}

const {
  navLinkList = [],
  isLoggedIn = false,
  userName = '',
} = defineProps<MobileNavMenuProps>();

const uiStore = useUIStore()
const route = useRoute()
const iframeStore = useIframeStore();

const menuRef = ref();
const megaMenuButtons = ref<typeof MobileNavMegaMenuButton[]>([]);
const isVisible = ref<boolean>(false);

const toggleMenu = () => {
  isVisible.value = !isVisible.value;
  uiStore.scrollLockBody(isVisible.value);
  iframeStore.sendMessageToChatbot('minimise', {
    currentMinimise: true
  });

  if (!isVisible.value) {
    for (const megaMenu of megaMenuButtons.value) {
      megaMenu.closeMegaMenu();
    }
  }
};

const menuLoseFocus = (event: FocusEvent) => {
  if (!event.relatedTarget || menuRef.value.contains(event.relatedTarget)) {
    return;
  }

  isVisible.value = false;
}

const onLinkClicked = () => {
  setTimeout(() => {
    isVisible.value = false;
  }, 300)
}

const menuStyling = computed(() => ({
  'height': `calc(100dvh - ${uiStore.navHeight}px)`
}));

</script>